// Duplicate (WOS-BUNDLE; Work OS `duplicateTask`, `duplicateContainerTree`): a task is copied
// next to itself as "<title> (copy)" with its tags, fields, description and (by default) its
// checklists, items reopened; the dialog asks for subtasks, files (with their folders, over the
// same bytes: TASK-FILES' `copyTaskFiles`) and
// assignees too. A board or a space is copied as "Copy of <name>" with its statuses, fields,
// views and settings; "Include tasks" copies the tasks as well (off: the toast says they were
// not). The drawer's menu, a list row's menu, the board and space menus (sidebar and headers)
// open these.
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Input,
  Label,
  Notice,
  toast,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { problemText } from "../settings/problem-text";
import { invalidateWork } from "./work-queries";

function Option({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="flex items-start gap-2.5">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(v) => onChange(v === true)}
        className="mt-0.5"
      />
      <Label htmlFor={id} className="flex flex-col items-start gap-0.5 font-normal">
        <span className="text-sm text-foreground">{label}</span>
        {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
      </Label>
    </div>
  );
}

/** "Duplicate…" for a task: what goes with the copy. */
export function DuplicateTaskDialog({
  api,
  company,
  task,
  open,
  onOpenChange,
  onOpenTask,
}: {
  api: Api;
  company: CompanyAccess;
  task: { id: string; title: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Opens the copy (the toast's Open). */
  onOpenTask?: ((id: string) => void) | undefined;
}) {
  const qc = useQueryClient();
  const [o, setO] = useState({ subtasks: false, checklists: true, files: false, assignees: false });
  const set = (k: keyof typeof o) => (on: boolean) => setO((x) => ({ ...x, [k]: on }));
  const copy = useMutation({
    mutationFn: () =>
      api.client.tasks.duplicate({ params: { co: company.id, id: task.id }, body: o }),
    onSuccess: async (t) => {
      onOpenChange(false);
      toast.add({
        title: `“${t.title}” is right next to it`,
        type: "success",
        ...(onOpenTask
          ? { actionProps: { children: "Open", onClick: () => onOpenTask(t.id) } }
          : {}),
      });
      await invalidateWork(qc, company.id);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    copy.mutate();
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !copy.isPending && onOpenChange(v)}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Duplicate task</DialogTitle>
            <DialogDescription>
              “{task.title} (copy)” lands right after it, with its description, fields and tags.
            </DialogDescription>
          </DialogHeader>
          <fieldset className="flex flex-col gap-3" aria-label="Copy with it">
            <Option
              label="Checklists"
              hint="Items start unticked"
              checked={o.checklists}
              onChange={set("checklists")}
            />
            <Option label="Subtasks" checked={o.subtasks} onChange={set("subtasks")} />
            <Option
              label="Files"
              hint="With their folders; the bytes are shared, not uploaded again"
              checked={o.files}
              onChange={set("files")}
            />
            <Option label="Assignees" checked={o.assignees} onChange={set("assignees")} />
          </fieldset>
          {copy.isError ? (
            <Notice tone="danger" role="alert">
              {problemText(copy.error, "The task was not copied.")}
            </Notice>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={copy.isPending}>
              {copy.isPending ? "Copying…" : "Duplicate"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** "Duplicate…" for a board or a space: the copy's name and whether its tasks come along. */
export function DuplicateContainerDialog({
  api,
  company,
  kind,
  container,
  open,
  onOpenChange,
}: {
  api: Api;
  company: CompanyAccess;
  kind: "board" | "space";
  container: { id: string; name: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const nameId = useId();
  const [name, setName] = useState(`Copy of ${container.name}`);
  const [tasks, setTasks] = useState(false);
  const co = company.id;
  const copy = useMutation({
    mutationFn: async () => {
      const body = { name: name.trim(), tasks };
      return kind === "board"
        ? api.client.boards.duplicate({ params: { co, id: container.id }, body })
        : api.client.spaces.duplicate({ params: { co, id: container.id }, body });
    },
    onSuccess: async (made) => {
      onOpenChange(false);
      toast.add({
        title: `${made.name} created`,
        description: tasks
          ? "Structure, settings and tasks copied."
          : "Structure and settings copied; tasks are not.",
        type: "success",
      });
      await invalidateWork(qc, co);
      void navigate(
        kind === "board"
          ? { to: "/c/$slug/work/b/$boardId", params: { slug: company.slug, boardId: made.id } }
          : { to: "/c/$slug/work/s/$spaceId", params: { slug: company.slug, spaceId: made.id } },
      );
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) copy.mutate();
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !copy.isPending && onOpenChange(v)}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Duplicate {kind}</DialogTitle>
            <DialogDescription>
              {kind === "board"
                ? "Copies its statuses, fields, views and settings."
                : "Copies its boards with their statuses, fields, views and settings. Access is not copied."}
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={nameId}>Name</FieldLabel>
            <Input
              id={nameId}
              value={name}
              maxLength={120}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Option
            label="Include tasks"
            hint="Off: an empty copy, ready for new work"
            checked={tasks}
            onChange={setTasks}
          />
          {copy.isError ? (
            <Notice tone="danger" role="alert">
              {problemText(copy.error, `The ${kind} was not copied.`)}
            </Notice>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={copy.isPending || !name.trim()}>
              {copy.isPending ? "Copying…" : "Duplicate"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
